import { stored } from '@/util/PStorage'
import PCrypto from '@/util/crypto/PCrypto'
import OnboardingAPI from '@/util/api/OnboardingAPI'
import { ResponseStatus } from '@/enum'
import EntryRepository from '@/data/entry-repository'

class Onboarding {
    /**
     * Attempt to login to the application
     */
    public unlock(password: string): boolean {
        if (!password) {
            throw Error('Password is empty')
        }
        // get PBK from local storage
        const userKey = PCrypto.PBK(password, stored.secretKey)
        // check if user key is correct
        if (PCrypto.SHA256(userKey) !== stored.userKeyHash) {
            throw Error('Incorrect password')
        }
        // decrypt and store private key
        stored.privateKey = PCrypto.AES.decrypt(stored.encryptedPrivateKey, userKey)
        return true
    }

    /**
     * Fetch and store API token from the server
     */
    public async fetchToken(): Promise<void> {
        try {
            const result = await OnboardingAPI.requestToken(stored.email, stored.userKeyHash)
            if (result.status === ResponseStatus.SUCCESS) {
                stored.apiToken = PCrypto.RSA.decrypt(result.apiToken!)
                stored.userId = result.userId!
                stored.emailVerified = !!result.emailVerified
            } else {
                throw Error(result.message)
            }
        } catch (err) {
            console.error(err)
            throw Error('Network authentication issue')
        }
    }

    /**
     * Save user details into local storage
     *
     * @param {string} email
     * @param {string} username
     */
    public setUserDetails(email: string, username: string): void {
        stored.email = email
        stored.username = username
    }

    /**
     * Send data for registration from local storage,
     * api_token is set after success
     *
     * @returns {Promise<void>}
     */
    public async register(): Promise<void> {
        try {
            const result = await OnboardingAPI.register({
                username: stored.username,
                email: stored.email,
                publicKey: stored.publicKey,
                privateKey: stored.encryptedPrivateKey,
                userKeyHash: stored.userKeyHash
            })
            if (result.status === ResponseStatus.SUCCESS) {
                stored.apiToken = PCrypto.RSA.decrypt(result.apiToken!)
                stored.userId = result.userId!
                stored.emailVerified = !!result.emailVerified
            } else {
                throw Error(`Failed to register: ${result.message}`)
            }
        } catch (err) {
            console.error(err)
            throw Error('Network authentication issue')
        }
    }

    /**
     * Create the default shared vault
     *
     * @returns {Promise<void>}
     */
    public async createDefaults(): Promise<void> {
        try {
            await EntryRepository.create('vault')
                .setData({
                    title: 'Shared',
                    icon: 'icon:folders',
                    tags: ['shared', 'team']
                })
                .setFields({
                    notes: 'This is where all new passwords and vaults go when '
                    + 'somebody gives you access.\nYou can then move them out at any time.'
                })
                .save()
        } catch (err) {
            console.error(err)
        }
    }

    /**
     * Check if a username is available
     *
     * @param {string} username
     * @returns {Promise<{ valid: boolean, error: string }>}
     */
    public async checkUsername(username: string): Promise<{ valid: boolean, error: string }> {
        let valid = false
        let error = ''
        if (username === '') {
            error = 'Username is empty'
            return { valid, error }
        }
        try {
            if (await OnboardingAPI.usernameExists(username)) {
                error = 'Username unavailable'
            } else {
                valid = true
            }
        } catch (err) {
            console.error(err)
            error = 'Network issue'
        }
        return { valid, error }
    }

    /**
     * Check if an email is available
     *
     * @param {string} email
     * @returns {Promise<{ valid: boolean, error: string }>}
     */
    public async checkEmail(email: string): Promise<{ valid: boolean, error: string }> {
        let valid = false
        let error = ''
        if (email === '') {
            error = 'Email is empty'
            return { valid, error }
        }
        try {
            if (await OnboardingAPI.emailExists(email)) {
                error = 'Email unavailable'
            } else {
                valid = true
            }
        } catch (err) {
            console.error(err)
            error = 'Network issue'
        }
        return { valid, error }
    }

    /**
     * Generate user keys and save to local storage
     *
     * @param {string} password
     */
    public generateKeys(password: string): void {
        const { publicKey, privateKey } = PCrypto.RSA.keypair()
        stored.publicKey = publicKey
        stored.privateKey = privateKey
        stored.secretKey = PCrypto.generateSecretKey()
        const userKey = PCrypto.PBK(password.toString(), stored.secretKey)
        stored.encryptedPrivateKey = PCrypto.AES.encrypt(privateKey, userKey)
        stored.userKeyHash = PCrypto.SHA256(userKey)
    }

    /**
     * Send data to link the current device and store
     * resulting keys into local storage
     *
     * @param {string} email
     * @param {string} secretKey
     * @param {string} password
     * @returns {Promise<boolean>}
     */
    public async link(email: string, secretKey: string, password: string): Promise<boolean> {
        const userKey = PCrypto.PBK(password, secretKey)
        const userKeyHash = PCrypto.SHA256(userKey)

        try {
            const result = await OnboardingAPI.link(email, userKeyHash)

            if (result.status === ResponseStatus.SUCCESS) {
                stored.userId = result.userId!
                stored.email = email
                stored.secretKey = secretKey
                stored.userKeyHash = userKeyHash
                stored.username = result.username!
                stored.publicKey = result.publicKey!
                stored.encryptedPrivateKey = result.encryptedPrivateKey!
                stored.privateKey = PCrypto.AES.decrypt(stored.encryptedPrivateKey, userKey)
                stored.apiToken = PCrypto.RSA.decrypt(result.apiToken!)
                return true
            }
            throw new Error('Link unsuccessful')
        } catch (err) {
            console.error(err)
            return false
        }
    }

    public async verifyEmail(verifyToken: string): Promise<void> {
        const [userId, token] = verifyToken.split(':')
        const result = await OnboardingAPI.verifyEmail(userId, token)

        if (result.status !== ResponseStatus.SUCCESS) {
            throw new Error(result.message)
        }
    }

    public async resendVerificationEmail(): Promise<void> {
        const result = await OnboardingAPI.resendVerification(stored.userId)

        if (result.status !== ResponseStatus.SUCCESS) {
            throw new Error(result.message)
        }
    }
}

export default new Onboarding()
